import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, Play, Pause, Check, Sparkles, Brain, Eye, Wind, Sun } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
  clarity: { icon: Brain, color: "#486a8f", bg: "#e7edf5", label: "Clarity" },
  awareness: { icon: Eye, color: "#7a5fb0", bg: "#ede8f5", label: "Awareness" },
  reflection: { icon: Sun, color: "#c18848", bg: "#f5ece0", label: "Reflection" },
};

export default function PracticesPage() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/exercises`);
        setList(r.data.exercises || []);
      } catch (e) {}
    })();
  }, []);

  const playVoice = async (text) => {
    try {
      setPlaying(true);
      const resp = await fetch(`${API}/voice/tts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) throw new Error("Voice error");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => setPlaying(false);
        await audioRef.current.play();
      }
    } catch (e) {
      toast.error("Voice failed.");
      setPlaying(false);
    }
  };

  const stopVoice = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  };

  const markComplete = async (ex) => {
    try {
      await axios.post(`${API}/exercises/${ex.id}/complete`, {});
      toast.success("Practice complete.");
      stopVoice();
      setSelected(null);
    } catch (e) {}
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="sticky top-0 z-40 bg-[#f7f3ea]/85 backdrop-blur-md border-b border-[#e9e0cd]">
        <div className="max-w-5xl mx-auto px-6 h-[68px] flex items-center justify-between">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]"
          >
            <ArrowLeft size={16} /> Dashboard
          </Link>
          <div className="flex items-center gap-1">
            <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
            <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <div className="mb-10">
          <p className="font-mono-eyebrow text-[#486a8f] mb-3">Awareness & clarity</p>
          <h1 className="font-display text-[36px] md:text-[48px] leading-[1.05] text-[#0f1724] tracking-tight">
            Practices to{" "}
            <span className="italic font-light text-[#486a8f]">clear the mind</span>.
          </h1>
          <p className="font-body text-[15px] text-[#4b5563] mt-4 max-w-2xl leading-relaxed">
            Small, repeatable practices designed to build awareness and return you to yourself.
            Tap any to read or press play to hear your coach guide you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {list.map((ex) => {
            const meta = CATEGORY_META[ex.category] || CATEGORY_META.clarity;
            const Icon = meta.icon;
            return (
              <button
                key={ex.id}
                onClick={() => setSelected(ex)}
                className="group text-left rounded-2xl bg-white border border-[#eadfc9] p-6 hover:border-[#d9cfb8] card-hover"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: meta.bg }}
                  >
                    <Icon size={16} style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono-eyebrow" style={{ color: meta.color }}>
                      {meta.label} · {ex.duration_min} min
                    </p>
                    <h3 className="font-display text-[20px] text-[#0f1724] tracking-tight leading-tight">
                      {ex.title}
                    </h3>
                  </div>
                </div>
                <p className="font-body text-[13px] text-[#4b5563] leading-relaxed">
                  {ex.description}
                </p>
              </button>
            );
          })}
        </div>
      </main>

      {/* Practice modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-[#0f1724]/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {
            stopVoice();
            setSelected(null);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl rounded-2xl bg-[#faf6ec] border border-[#eadfc9] p-8 md:p-10"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-[#486a8f]" />
              <p className="font-mono-eyebrow text-[#486a8f]">{selected.duration_min} min practice</p>
            </div>
            <h2 className="font-display text-[28px] md:text-[34px] text-[#0f1724] tracking-tight leading-tight mb-5">
              {selected.title}
            </h2>
            <p className="font-body text-[16px] md:text-[17px] text-[#2b3442] leading-relaxed whitespace-pre-wrap">
              {selected.script}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {!playing ? (
                <Button
                  onClick={() => playVoice(selected.script)}
                  className="h-12 px-6 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white btn-lift"
                >
                  <Play size={15} className="mr-2" /> Play with your coach's voice
                </Button>
              ) : (
                <Button
                  onClick={stopVoice}
                  className="h-12 px-6 rounded-full bg-[#b94e4e] hover:bg-[#a33f3f] text-white"
                >
                  <Pause size={15} className="mr-2" /> Stop
                </Button>
              )}
              <Button
                onClick={() => markComplete(selected)}
                variant="ghost"
                className="h-12 px-6 rounded-full text-[#3e6b52] hover:bg-[#e9f0ec]"
              >
                <Check size={15} className="mr-2" /> Mark complete
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  stopVoice();
                  setSelected(null);
                }}
                className="h-12 px-4 rounded-full text-[#6b7280]"
              >
                Close
              </Button>
            </div>

            <audio ref={audioRef} hidden />
          </div>
        </div>
      )}
    </div>
  );
}
